import React from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Zap, Flame, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";

export const DemoPlayerModal = ({ player, onClose }) => {
    const { t } = useTranslation();
    const { brand } = useBrandConfig();

    if (!player) return null;

    const getRiskDetails = (points) => {
        if (points >= 800) return {
            labelKey: "holy.betting.risk_legendary",
            descKey: "holy.player_modal.risk_legendary_desc",
            Icon: Flame,
            bgColor: brand.colors.goalFlash,
            textColor: "#000000",
        };
        if (points >= 400) return {
            labelKey: "holy.betting.risk_bold",
            descKey: "holy.player_modal.risk_bold_desc",
            Icon: Zap,
            bgColor: brand.colors.highlight,
            textColor: brand.colors.highlightText,
        };
        return {
            labelKey: "holy.betting.risk_safe",
            descKey: "holy.player_modal.risk_safe_desc",
            Icon: ShieldCheck,
            bgColor: brand.colors.accent,
            textColor: brand.colors.accentText,
        };
    };

    const risk = getRiskDetails(player.points);

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 font-body">

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className="relative w-full max-w-sm bg-white/90 backdrop-blur-2xl rounded-[2.5rem] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.2)] overflow-hidden"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-10 h-10 bg-black/5 hover:bg-black/10 rounded-full flex items-center justify-center transition-colors text-black/50 hover:text-black"
                    >
                        <X size={20} strokeWidth={3} />
                    </button>

                    <div className="flex flex-col items-center text-center mt-4 mb-6">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-3 text-2xl font-heading italic text-black/30">
                            #{player.rank}
                        </div>
                        <h3 className="text-2xl font-black text-black uppercase tracking-wide">{player.pseudo}</h3>
                        <p className="text-[10px] font-black text-black/40 uppercase tracking-[0.2em] mt-1">
                            {player.isMe ? t('holy.player_modal.my_prono') : t('holy.player_modal.opponent')}
                        </p>
                    </div>

                    <div className="bg-white rounded-3xl p-5 flex flex-col items-center shadow-sm mb-4">
                        <span className="text-[10px] font-black text-black/30 uppercase tracking-[0.2em] mb-1">
                            {t('holy.player_modal.score_label')}
                        </span>
                        <div className="text-[3.5rem] font-heading italic tracking-tighter text-black leading-none drop-shadow-sm">
                            {player.betDisplay}
                        </div>
                    </div>

                    <div className="rounded-3xl p-5 flex flex-col items-start shadow-sm" style={{ backgroundColor: risk.bgColor, color: risk.textColor }}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="bg-white/30 p-1.5 rounded-full">
                                <risk.Icon size={16} strokeWidth={2.5} />
                            </div>
                            <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                                {t('holy.player_modal.risk_label', { label: t(risk.labelKey) })}
                            </span>
                        </div>
                        <p className="text-xs font-bold leading-relaxed opacity-80">{t(risk.descKey)}</p>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
